import { useState } from 'react';
import { Box } from '@mui/material';
import { SrOnlyText, TextBlock, TextSpan } from '../../../Atoms/Text';
import { Toggle } from '../../../Atoms/Toggle';
import { ConfirmDialog } from '../ConfirmDialog';
import { EngagementStepFooter } from './EngagementStepFooter';
import { EngagementConfiguration, EngagementStep } from '../../../../types/advise';
import { updateAdviseEngagementConfiguration } from '../../../../Api/Advise/Engagements';
import { useSnackbar } from '../../../../Context/SnackbarContext';

export type ConfigurationExit = 'next' | 'quit';

export interface EngagementConfigurationScreenProps {
  engagementId: string;
  steps: EngagementStep[];
  onSaved: (configuration: EngagementConfiguration, exit: ConfigurationExit) => void;
  onExit: () => Promise<void> | void;
  // True while the page waits on the step answers before leaving, so the footer can't send the save twice.
  exiting?: boolean;
}

export const EngagementConfigurationScreen = ({
  engagementId,
  steps,
  onSaved,
  onExit,
  exiting = false
}: EngagementConfigurationScreenProps) => {
  const [enabled, setEnabled] = useState<Record<string, boolean>>(() =>
    Object.fromEntries(steps.map((step) => [step.key, step.enabled]))
  );
  const [saving, setSaving] = useState(false);
  const busy = saving || exiting;
  const [confirmingExit, setConfirmingExit] = useState(false);
  const { setSnack } = useSnackbar();

  const save = async (exit: ConfigurationExit) => {
    setSaving(true);
    const optional = steps.filter((step) => !step.required).map((step) => [step.key, enabled[step.key]]);
    const { data, error } = await updateAdviseEngagementConfiguration(engagementId, Object.fromEntries(optional));
    setSaving(false);
    if (error || !data) {
      setSnack({ message: 'Failed to save the configuration. Please try again.', open: true, type: 'error' });
      return;
    }
    onSaved(data, exit);
  };

  return (
    <Box
      className="mx-auto max-w-3xl p-6 rounded-lg bg-white"
      data-testid="engagement-configuration">
      <TextBlock
        size="base"
        weight="semibold"
        color="grey8">
        Configure your engagement
      </TextBlock>
      <TextBlock
        size="sm14"
        color="grey6"
        className="mt-1 mb-4">
        Select which steps to include. Steps marked Required cannot be removed.
      </TextBlock>
      <ol>
        {steps.map((step, index) => (
          <Box
            component="li"
            key={step.key}
            className={`flex items-center gap-3 px-6 py-3 ${index % 2 === 0 ? 'bg-grey-1' : ''}`}
            sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <TextSpan
              size="xs"
              color="white"
              className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full ${
                step.required ? 'bg-grey-8' : 'bg-indigo'
              }`}>
              {index + 1}
            </TextSpan>
            <div className="flex-1">
              <TextBlock
                size="sm14"
                weight="semibold"
                color="grey8">
                {step.label}
              </TextBlock>
              {step.description && (
                <TextBlock
                  size="xs"
                  color="grey6">
                  {step.description}
                </TextBlock>
              )}
            </div>
            {step.required ? (
              // The palette's green maps to no Tailwind class, so the shade comes from className.
              <TextSpan
                size="xs"
                weight="medium"
                color="green"
                className="rounded bg-green-50 px-2 py-1 text-green-700">
                Required
              </TextSpan>
            ) : (
              <label
                className="flex cursor-pointer"
                htmlFor={`engagement-step-${step.key}`}>
                <SrOnlyText isSpan>{`Include ${step.label}`}</SrOnlyText>
                <Toggle
                  id={`engagement-step-${step.key}`}
                  dataTestId={`engagement-step-${step.key}`}
                  width={9}
                  height={5}
                  checked={enabled[step.key]}
                  onChange={(event) => {
                    if (!busy) setEnabled((prev) => ({ ...prev, [step.key]: event.target.checked }));
                  }}
                />
              </label>
            )}
          </Box>
        ))}
      </ol>
      <EngagementStepFooter
        onCancel={() => setConfirmingExit(true)}
        onSaveAndQuit={() => save('quit')}
        onNext={() => save('next')}
        saving={busy}
      />
      <ConfirmDialog
        open={confirmingExit}
        title="Are you sure you wish to exit?"
        message="Your progress will be lost."
        cancelLabel="Keep editing"
        confirmLabel="Exit"
        confirmColor="primary"
        onCancel={() => setConfirmingExit(false)}
        onConfirm={onExit}
      />
    </Box>
  );
};
